const STORAGE_PREFIX = "konstrulog.diary.draftsBannerDismissed.v1:";
/** Drop stale project entries so the map does not grow forever. */
const ENTRY_TTL_MS = 1000 * 60 * 60 * 24 * 180; // 180 days

type DismissEntry = {
  countAtDismiss: number;
  updatedAt: number;
};

type DismissMap = Record<string, DismissEntry>;

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function isFresh(entry: DismissEntry, now = Date.now()): boolean {
  return now - entry.updatedAt < ENTRY_TTL_MS;
}

function pruneMap(map: DismissMap, now = Date.now()): DismissMap {
  const next: DismissMap = {};
  for (const [projectId, entry] of Object.entries(map)) {
    if (isFresh(entry, now)) next[projectId] = entry;
  }
  return next;
}

function readMap(userId: string): DismissMap {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return pruneMap(parsed as DismissMap);
  } catch {
    return {};
  }
}

function writeMap(userId: string, map: DismissMap): void {
  const pruned = pruneMap(map);
  const key = storageKey(userId);
  try {
    if (Object.keys(pruned).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(pruned));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Returns dismiss state for a project, or null if not dismissed / expired. */
export function getDiaryDraftsBannerDismissed(
  userId: string,
  projectId: string,
): DismissEntry | null {
  const map = readMap(userId);
  const entry = map[projectId];
  if (!entry) {
    writeMap(userId, map);
    return null;
  }
  writeMap(userId, map);
  return entry;
}

export function setDiaryDraftsBannerDismissed(
  userId: string,
  projectId: string,
  countAtDismiss: number,
): void {
  const map = readMap(userId);
  map[projectId] = { countAtDismiss, updatedAt: Date.now() };
  writeMap(userId, map);
}

/** Removes one project entry; deletes the user key when the map is empty. */
export function clearDiaryDraftsBannerDismissed(userId: string, projectId: string): void {
  const map = readMap(userId);
  if (!(projectId in map)) {
    writeMap(userId, map);
    return;
  }
  delete map[projectId];
  writeMap(userId, map);
}

/** Clears all dismissals for a user (call on sign-out). */
export function clearAllDiaryDraftsBannerDismissed(userId: string): void {
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    /* ignore */
  }
}
